import React, { useEffect, useMemo, useState } from 'react';
import { ICON } from '../../icons/iconIds';
import IconButton from '../icons/IconButton';
import Card from '../ui/Card';
import Button from '../ui/Button';
import DrillHoleMap from '../DrillHoleMap';
import { normalizeReportMapViewport } from '../../utils/dailyReport/reportMapViewport';
import './ReportMapZoomModal.css';

const REPORT_STATUS_FILTERS = {
  Complete: true,
  'In Progress': true,
  Planned: false,
  Lost: true,
};

const ReportMapZoomModal = ({
  open,
  onClose,
  mapContext,
  savedViewport,
  onSave,
}) => {
  const [draftViewport, setDraftViewport] = useState(savedViewport);
  const [mapKey, setMapKey] = useState(0);
  const [fitMode, setFitMode] = useState('saved');

  useEffect(() => {
    if (open) {
      setDraftViewport(savedViewport);
      setFitMode(savedViewport ? 'saved' : 'auto');
    }
  }, [open, savedViewport]);

  const initialViewport = useMemo(() => {
    if (fitMode === 'auto') return null;
    return normalizeReportMapViewport(savedViewport);
  }, [fitMode, savedViewport, mapKey]);

  if (!open || !mapContext?.hasMapData) {
    return null;
  }

  const handleReset = () => {
    setFitMode('auto');
    setDraftViewport(null);
    setMapKey((value) => value + 1);
  };

  const handleSave = () => {
    onSave(normalizeReportMapViewport(draftViewport));
    onClose();
  };

  return (
    <div className="modal-overlay report-map-zoom-overlay" onClick={onClose} role="presentation">
      <div onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="Set report map zoom">
        <Card className="modal-content report-map-zoom-modal">
          <div className="report-map-zoom-modal__header">
            <div>
              <h3>Set Report Map Zoom</h3>
              <p className="report-map-zoom-modal__subtitle">
                Pan and zoom the map, then save. The PDF Current Drilling Locations slide uses this view.
              </p>
            </div>
            <IconButton icon={ICON.uiClose} className="close-button" onClick={onClose} ariaLabel="Close" />
          </div>

          <div className="report-map-zoom-modal__map">
            <DrillHoleMap
              key={mapKey}
              data={mapContext.holes}
              filterType="zone"
              zoneColorMap={mapContext.zoneColorMap}
              zoneFilters={mapContext.zoneFilters}
              statusFilters={REPORT_STATUS_FILTERS}
              exportBoundsHoles={mapContext.exportBoundsHoles}
              initialViewport={initialViewport}
              onViewportChange={setDraftViewport}
              showLegend={false}
              smoothZoom
            />
          </div>

          <div className="report-map-zoom-modal__meta">
            {draftViewport ? (
              <span>
                View: {draftViewport.lat.toFixed(5)}, {draftViewport.lon.toFixed(5)} · zoom{' '}
                {draftViewport.zoom.toFixed(2)}
              </span>
            ) : (
              <span>Automatic framing (in-progress collars with padding, all holes visible)</span>
            )}
          </div>

          <div className="report-map-zoom-modal__actions">
            <Button onClick={handleSave}>Save View</Button>
            <Button variant="secondary" onClick={handleReset}>
              Reset to Auto
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ReportMapZoomModal;
